<template>
  <BaseButton :loading="isExporting" class="ml-auto" @click="handleExport">
    <BaseIcon
      v-if="isExported"
      icon="check-circle"
      tag="i"
      :font-scale="0.85"
      class="ml-n2"
    />
    Download
  </BaseButton>
</template>

<script>
import { defineComponent, computed, ref } from '@vue/composition-api'
import { createVuexHelpers } from 'vue2-helpers'
import { format } from 'date-fns'
import * as XLSX from 'xlsx'
import { uniqBy } from 'lodash-es'

import { DATE_FORMAT } from '@constants'
import { LincolnBDXService, LincolnGlobalUMRService } from '@services'
import {
  useCurrentUser,
  useUserCompany,
  useToast,
  useTryCatch
} from '@composables'

import { BaseButton, BaseIcon } from '@components/base'
import { getBDXManagementTableColumns } from './BDXManagementTable.constants'

export default defineComponent({
  name: 'BDXManagementTableExportButton',
  components: {
    BaseButton,
    BaseIcon
  },
  props: {
    filters: {
      type: Object,
      required: () => ({})
    },
    useRemoteExport: {
      type: Boolean,
      default: true
    }
  },
  setup(props) {
    const { useGetters } = createVuexHelpers()

    const { bdxFilters } = useGetters(['bdxFilters'])

    const { userEmail, isSuperUser } = useCurrentUser()
    const { roleName: userCompanyRoleName, roleValue: userCompanyRoleValue } =
      useUserCompany()
    const { showToast } = useToast()

    const isExporting = ref(false)
    const isExported = ref(false)

    const BDXManagementTableColumns = computed(() =>
        getBDXManagementTableColumns(isSuperUser.value)
    )

    const exportPayload = computed(() => {
      const [createdOnStart, createdOnEnd] = props.filters.created_on || []

      const lastReportDateFilters = props.filters.filters.reduce((acc, {key, value}) => {
        if (key === 'last_report_date') {
          const [lastReportDateStart, lastReportDateEnd] = value;
          return {
            ...acc, 
            ...(!!lastReportDateStart && {
              last_report_date_gt: format(lastReportDateStart, DATE_FORMAT)
            }),
            ...(!!lastReportDateEnd && {
              last_report_date_lt: format(lastReportDateEnd, DATE_FORMAT)
            }),
          }
        } 
        return acc;
      }, {});

      const columnFilters = {
        bdx_status: [props.filters.bdx_status],
        fcp_status: [props.filters.fcp_status],
        claim_status: props.filters.claim_status,
        payment_status: props.filters.payment_status,
        reserve: props.filters.reserve,
        ...(!!createdOnEnd && {
          created_on_lt: format(createdOnEnd, DATE_FORMAT)
        }),
        ...(!!createdOnStart && {
          created_on_gt: format(createdOnStart, DATE_FORMAT)
        }),
        search: props.filters.search,
        sort: props.filters.sortBy
          ? `${props.filters.sortDesc ? '-' : ''}${props.filters.sortBy}`
          : 'created_on',
        ...lastReportDateFilters,
      }

      if (!!props.filters.filters.length) {
        props.filters.filters.forEach((filter) => {
          if (!!columnFilters[filter.key]) {
            columnFilters[filter.key].push(filter.value)
          } else if (filter.key !== 'last_report_date') {
            columnFilters[filter.key] = [filter.value]
          }
        })
      }
      return columnFilters
    })

    const buildLocalExportData = (values) => {
      return values.map((value) => {
        return BDXManagementTableColumns.value.reduce((acc, {key}) => {
          if (key === 'bdx_status') {
            acc[key] = value.bdx_status ? 'Active' : 'Inactive';
          }
          if (key === 'billing_status') {
            acc[key] = value.billing_status ? 'Active' : 'Inactive';
          }
          if (key === 'billing_type') {
            acc[key] = value.billing_type || '';
          }
          if (key === 'fcp_status') {
            acc[key] = value.fcp_status ? 'Active' : 'Inactive';
          }
          if (key === 'rec_status') {
            acc[key] = value.rec_status ? 'Active' : 'Inactive';
          }
          if (key === 'created_on') {
            acc[key] = format(value.created_on, DATE_FORMAT);
          } 
          if (key === 'last_report_date') {
            acc[key] = format(value.last_report_date, DATE_FORMAT);
          } 
          if (key === 'umr_rc_cc_sn') {
            acc[key] = value.umr_rc_cc_sn; 
          } 
          if (key === 'umr') {
            acc[key] = value.umr || '';
          } 
          if (key === 'risk_code') {
            acc[key] = value.risk_code || '';
          } 
          if (key === 'cat_code') {
            acc[key] = value.cat_code || '';
          } 
          if (key === 'section_no') {
            acc[key] = value.section_no || '';
          } 
          if (key === 'tpa') {
            acc[key] = value.tpa ? value.tpa.TPA : ''
          } 
          if (key === 'underwriter_lead') {
            acc[key] = value.underwriter
            .filter((uw) => uw.lead)
            .map(
              (uw) => uw.rls_underwriter.underwriter
            )
            .join(', ');
          } 
          if (key === 'underwriter_followers') {
            acc[key] = value.underwriter
            .filter((uw) => !uw.lead)
            .map(
              (uw) => uw.rls_underwriter.underwriter
            )
            .join(', ');
          }
          // Lead is per SYNDICATE, not per underwriter - one market can lead
          // on one syndicate and follow on another - so these two split an
          // underwriter's own syndicate list rather than the underwriter list.
          // A line nobody has ruled on falls back to its market's flag, which
          // is how every pre-existing row reads. Both branches are required:
          // a column with no branch here contributes no key to the row object,
          // and the header row is written from the full column list, so every
          // label to the right of a missing one lands over the wrong data.
          if (key === 'lead_syndicate' || key === 'follower_syndicates') {
            const wantLead = 'lead_syndicate' === key;
            acc[key] = value.underwriter
            .flatMap((uw) => (uw.syndicates || [])
              .filter((s) => (
                null === s.lead || undefined === s.lead
                  ? !!uw.lead === wantLead
                  : !!s.lead === wantLead
              ))
              .map((s) => s.syndicate_no || '—')
            )
            .join(', ');
          }
          if (key === 'coverholder') {
            acc[key] = value.coverholder ? value.coverholder.coverholder : ''
          } 
          if (key === 'broker') {
            acc[key] = value.broker ? value.broker.broker : ''
          } 
          if (key === 'ucr') {
            acc[key] = value.ucr || '';
          } 
          if (key === 'account_type') {
            acc[key] = value.account_type || ''
          } 
          if (key === 'account_code') {
            acc[key] = value.account_code || '';
          } 
          if (key === 'account_name') {
            acc[key] = value.account_name || '';
          } 
          if (key === 'currency_type') {
            acc[key] = value.currency_type || '';
          }
          return acc;
        }, {});
      })
    }

    const handleExport = async () => {
      isExporting.value = true
      isExported.value = false

      await useTryCatch(async () => {
        const companyRole = isSuperUser.value
          ? bdxFilters.value.companyRoleName
          : userCompanyRoleName.value
        const companyRoleValue = isSuperUser.value
          ? bdxFilters.value.companyRoleValue
          : userCompanyRoleValue.value

        if (props.useRemoteExport) {
          await LincolnBDXService.export(
            companyRole,
            companyRoleValue,
            userEmail.value,
            exportPayload.value
          )

          showToast(
            'The billing status export is in process, and you will receive an update via email shortly!'
          )
        } else {
          const { data } = await LincolnGlobalUMRService.list(
            companyRole,
            companyRoleValue,
            {
              ...exportPayload.value,
              page_size: 1000,
            }
          )
          const { results } = data || {}

          const exportHeader = [BDXManagementTableColumns.value.map(({label}) => {
            return label 
          })];

          const exportData = buildLocalExportData(results)

          const workbook = XLSX.utils.book_new()

          const worksheet = XLSX.utils.json_to_sheet(exportData)

          XLSX.utils.sheet_add_aoa(worksheet, exportHeader);

          XLSX.utils.book_append_sheet(workbook, worksheet, 'BDX List')

          XLSX.writeFile(workbook, 'BDX List.xlsx')
        }

        isExported.value = true
      })

      isExporting.value = false
    }

    return {
      isExporting,
      isExported,
      handleExport
    }
  }
})
</script>

<style scoped>
.btn >>> .base-icon {
  width: 14px;
  height: 14px;
  margin-right: 8px;
}
</style>
